/**
 * Calendar invitations — THE single mutation path (PLAN §3.4): every surface
 * (calendar grid/popover, bell dropdown + notifications modal, chat card)
 * calls `respondToInvitation` here and nothing talks to the backend any other
 * way. Wire contract verbatim from the backend implementation
 * (.ai/tasks/2026-08-28_calendar_tool/IMPLEMENTATION.backend.md), including
 * its four interpretations: organizer cancel is un-gated; re-invite after
 * cancel revives to pending; a late FIRST response is allowed; ≤50
 * invitees per create call (enforced server-side, mirrored here).
 *
 * State: a small zustand store of the viewer's OWN invitations (pending +
 * active) that ghost chips and chat cards read, plus a per-appointment cache
 * of the organizer's sent rows. Realtime `appointment_*` notifications and
 * the user-channel REJOIN gap-fill (NotificationIcon.js pattern — the
 * `notification` push has no replay, so a reconnect refetches) keep it fresh;
 * accepted invitations MATERIALIZE a local event record from the snapshot
 * with `origin` set (§3.2), later patched by origin.appointment_uid.
 *
 * Plain API calls through apiClient (normal timeouts — these are not
 * provider/long-poll requests). No static @tauri-apps imports (guard list).
 */
import i18n from '../i18n';
import { create } from 'zustand';
import { apiClient } from '../utils/apiClient';
import {
  addNotificationListener,
  addUserRejoinListener,
  addSessionEndedListener,
} from './channelService';
import { listEvents, saveEvent, deleteEvent } from '../extensions/bundled/calendar/calendarStore';
import { bumpCalendarEvents } from '../extensions/bundled/calendar/calendarUiStore';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { setUpgradeModalOpen, setUpgradeReason } from '../store/uiModalStore';

export const MAX_INVITEES_PER_CREATE = 50;

/**
 * ONE predicate for the v1 recurrence×invites block (P4-N1): a recurring
 * MASTER and a per-instance OVERRIDE record are both uninvitable — an
 * override is part of a series even though its own `recurrence` is null,
 * which is exactly how it escaped the three gates before this fold-in.
 */
export function invitesBlockedFor(event) {
  return Boolean(event?.recurrence || event?.override_of);
}

/**
 * ONE error surface for invite mutations (polish round): a plan-limit 402
 * (`upgrade_required`) opens the PlanUpgradeModal exactly like the app's
 * existing sites (TeamsModal 402 branch → uiModalStore), NEVER a raw toast;
 * everything else toasts the server's named error string verbatim (the
 * backend's 422s — oversize snapshot, cap, validation — are human-written).
 */
export function surfaceInviteError(err, fallbackMessage) {
  if (err?.response?.status === 402 || err?.response?.data?.upgrade_required) {
    setUpgradeReason(err?.response?.data?.error || i18n.t('calendar.invite.cloudPlanRequired'));
    setUpgradeModalOpen(true);
    return 'upgrade';
  }
  useNotificationStore
    .getState()
    .error(err?.response?.data?.error || err?.message || fallbackMessage);
  return 'toast';
}

export const useCalendarInvitesStore = create((set) => ({
  pending: [], // my pending invitation rows (with snapshot)
  active: [], // my pending+accepted rows (gap-fill source)
  sentByAppointment: {}, // organizer view: appointment_uid -> rows
  setPending: (pending) => set({ pending }),
  setActive: (active) => set({ active }),
  setSent: (appointmentUid, rows) =>
    set((s) => ({ sentByAppointment: { ...s.sentByAppointment, [appointmentUid]: rows } })),
  reset: () => set({ pending: [], active: [], sentByAppointment: {} }),
}));

// Session-derived cache — clears when the account signs out (channelService
// invariant, the chatNotification.js module-load registration precedent).
addSessionEndedListener(() => {
  useCalendarInvitesStore.getState().reset();
});

/* ── snapshot ⇄ event helpers ─────────────────────────────────────────────── */

/** `<dateKey>T00:00:00Z` (+`plusDays`) — the v2 all-day instant derivation. */
function dateKeyToUtcInstant(dateKey, plusDays = 0) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateKey || ''));
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + plusDays));
  return d.toISOString();
}

/**
 * The snapshot for an event record — SHAPE v2 (backend fix round 1, enforced
 * server-side): `start`/`end` are ALWAYS ISO UTC instants; when `all_day` the
 * snapshot ALSO carries `start_date`/`end_date` (bare YYYY-MM-DD, end
 * INCLUSIVE) and the instants must equal the deterministic derivation
 * `<start_date>T00:00:00Z` / `<end_date + 1 day>T00:00:00Z` — the backend
 * 422s on any mismatch, and on date keys present for a timed snapshot.
 */
export function buildSnapshotFromEvent(event) {
  const allDay = event.all_day === true;
  const snapshot = {
    title: event.title || '',
    start: allDay ? dateKeyToUtcInstant(event.start_date) : event.start,
    end: allDay ? dateKeyToUtcInstant(event.end_date || event.start_date, 1) : event.end,
    all_day: allDay,
    location: event.location || '',
    tz: event.tz || 'UTC',
  };
  if (allDay) {
    snapshot.start_date = event.start_date;
    snapshot.end_date = event.end_date || event.start_date;
  }
  return snapshot;
}

/**
 * Event-record fields for a snapshot (materialize / patch / ghost paths all
 * share it). All-day reads the v2 BARE DATE keys (record shape per §3.2 —
 * end_date inclusive), never the derived instants.
 */
export function snapshotToEventFields(snapshot) {
  const allDay = snapshot?.all_day === true;
  return {
    title: snapshot?.title || '',
    location: snapshot?.location || '',
    all_day: allDay,
    start: allDay ? null : snapshot?.start || null,
    end: allDay ? null : snapshot?.end || snapshot?.start || null,
    start_date: allDay ? snapshot?.start_date || null : null,
    end_date: allDay ? snapshot?.end_date || snapshot?.start_date || null : null,
    tz: snapshot?.tz || 'UTC',
  };
}

/* ── fetches ──────────────────────────────────────────────────────────────── */

export async function fetchPendingInvitations() {
  const res = await apiClient.get('/calendar/invitations/pending');
  const pending = res.data.invitations || [];
  useCalendarInvitesStore.getState().setPending(pending);
  return pending;
}

export async function fetchActiveInvitations(sinceVersion = null) {
  const params = sinceVersion != null ? { since_version: sinceVersion } : {};
  const res = await apiClient.get('/calendar/invitations/active', { params });
  const active = res.data.invitations || [];
  useCalendarInvitesStore.getState().setActive(active);
  return active;
}

export async function fetchSentInvitations(appointmentUid) {
  const res = await apiClient.get('/calendar/invitations/sent', {
    params: { appointment_uid: appointmentUid },
  });
  const rows = res.data.invitations || [];
  useCalendarInvitesStore.getState().setSent(appointmentUid, rows);
  return rows;
}

/* ── mutations ────────────────────────────────────────────────────────────── */

/**
 * Organizer: create invitations for user ids OR a chat channel (dm/group).
 * 201 → rows cached under the appointment. 402 = cloud-gated (free organizer),
 * surfaced to the caller untouched — the server IS the enforcement.
 */
export async function createInvitations({ appointmentUid, snapshot, userIds, channelId, recurring = false }) {
  // Phase 4 defense in depth (owner line): recurring masters can't be shared
  // in v1 — snapshots stay single-event v2 shape, backend untouched.
  if (recurring) {
    throw new Error(i18n.t('calendar.invite.recurringNotShareableError'));
  }
  const body = { appointment_uid: appointmentUid, snapshot };
  if (channelId != null) {
    body.channel_id = channelId;
  } else {
    // The backend caps a create call at 50 invitees (interp. 4). SURFACED,
    // never silently truncated (tester N3): dropping picks without a word
    // invites people the organizer thinks are invited — worse than an error.
    if ((userIds || []).length > MAX_INVITEES_PER_CREATE) {
      throw new Error(
        i18n.t('calendar.invite.tooManyInvitees', {
          max: MAX_INVITEES_PER_CREATE,
          over: userIds.length - MAX_INVITEES_PER_CREATE,
        })
      );
    }
    body.user_ids = userIds || [];
  }
  const res = await apiClient.post('/calendar/invitations', body);
  const rows = res.data.invitations || [];
  useCalendarInvitesStore.getState().setSent(appointmentUid, rows);
  return rows;
}

/**
 * THE single response path: accept|decline by token, from ANY surface.
 * On accept, materializes the local event copy from the invitation's snapshot
 * (uid = appointment_uid, `origin` set) so it lands in the calendar at once;
 * on decline the ghost simply disappears with the refreshed pending list.
 *
 * @param context optional invitation row for surfaces that carry their own
 *   copy (the bell notification's `data` holds snapshot + appointment_uid) —
 *   used when the store hasn't loaded the row; otherwise the gap-fill sync
 *   materializes (it creates missing copies for accepted rows).
 */
export async function respondToInvitation(token, action, context = null) {
  if (action !== 'accept' && action !== 'decline') {
    throw new Error(`unknown response "${action}"`);
  }
  const res = await apiClient.post(`/calendar/invitations/${action}`, { token });
  if (action === 'accept') {
    const state = useCalendarInvitesStore.getState();
    const row =
      state.pending.find((r) => r.token === token) ||
      state.active.find((r) => r.token === token) ||
      context;
    if (row?.snapshot) await materializeAcceptedInvitation(row);
    else await syncInvitations();
  }
  await fetchPendingInvitations().catch(() => {});
  bumpCalendarEvents();
  return res.data;
}

/** Organizer: PATCH new snapshot (server bumps versions, resets on time change). */
export async function updateAppointment(appointmentUid, snapshot) {
  const res = await apiClient.patch(`/calendar/appointments/${appointmentUid}`, { snapshot });
  return res.data;
}

/** Organizer: cancel (terminal for rows; NOT cloud-gated — backend interp. 1). */
export async function cancelAppointment(appointmentUid) {
  const res = await apiClient.delete(`/calendar/appointments/${appointmentUid}`);
  return res.data;
}

/* ── invitee-side materialization / patching ──────────────────────────────── */

/**
 * ONE resolver for the organizer identity across the two wire shapes (tester
 * N1/F2): fetched pending/active rows carry `organizer: {id,name,email}`;
 * bell notification `data` carries `invited_by: {id,...}`; a bare
 * `organizer_id` wins if a payload ever sends it.
 */
export function organizerOfRow(row) {
  return {
    id: row?.organizer_id ?? row?.organizer?.id ?? row?.invited_by?.id ?? null,
    name: row?.organizer?.name ?? row?.invited_by?.name ?? null,
  };
}

async function materializeAcceptedInvitation(row) {
  await saveEvent({
    uid: row.appointment_uid,
    ...snapshotToEventFields(row.snapshot),
    calendar: 'personal',
    reminders: [],
    status: 'confirmed',
    origin: {
      organizer_id: organizerOfRow(row).id,
      appointment_uid: row.appointment_uid,
      snapshot_version: row.snapshot_version ?? 1,
    },
  });
}

/** My materialized copy for an appointment, if any (uid or origin match). */
async function findMaterializedCopy(appointmentUid) {
  const events = await listEvents().catch(() => []);
  return (
    events.find(
      (e) => e.origin?.appointment_uid === appointmentUid || e.uid === appointmentUid
    ) || null
  );
}

async function patchCopyFromSnapshot(appointmentUid, snapshot, snapshotVersion) {
  const copy = await findMaterializedCopy(appointmentUid);
  if (!copy?.origin) return false;
  if (snapshotVersion != null && (copy.origin.snapshot_version ?? 0) >= snapshotVersion) {
    return false; // already current — LWW by version
  }
  await saveEvent({
    ...copy,
    ...snapshotToEventFields(snapshot),
    origin: { ...copy.origin, snapshot_version: snapshotVersion ?? copy.origin.snapshot_version },
  });
  return true;
}

/* ── realtime + rejoin gap-fill ───────────────────────────────────────────── */

async function handleAppointmentNotification(notif) {
  const type = notif?.type;
  const data = notif?.data || {};
  try {
    if (type === 'appointment_invitation') {
      await fetchPendingInvitations();
    } else if (type === 'appointment_updated') {
      if (data.responses_reset) {
        // My acceptance was reset to pending (start/end changed): the copy
        // comes OFF the calendar and the ghost chip returns via pending.
        const copy = await findMaterializedCopy(data.appointment_uid);
        if (copy?.origin) await deleteEvent(copy.uid);
      } else {
        await patchCopyFromSnapshot(data.appointment_uid, data.snapshot, data.snapshot_version);
      }
      await fetchPendingInvitations();
    } else if (type === 'appointment_cancelled') {
      const copy = await findMaterializedCopy(data.appointment_uid);
      if (copy?.origin) await saveEvent({ ...copy, status: 'cancelled' });
      await fetchPendingInvitations();
    } else if (type === 'appointment_response') {
      // Organizer surface: refresh the sent cache if we hold it.
      const cached = useCalendarInvitesStore.getState().sentByAppointment[data.appointment_uid];
      if (cached) await fetchSentInvitations(data.appointment_uid);
      return; // no local event change
    } else {
      return;
    }
    bumpCalendarEvents();
  } catch {
    /* refetch paths are best-effort; the rejoin gap-fill catches up */
  }
}

/**
 * Rejoin gap-fill (NotificationIcon.js:70-88 pattern): refetch pending +
 * active and patch any stale materialized copy by snapshot_version.
 */
export async function syncInvitations() {
  const { isAuthenticated } = useAuthStore.getState();
  if (!isAuthenticated) return;
  try {
    const [, active] = await Promise.all([
      fetchPendingInvitations(),
      fetchActiveInvitations(),
    ]);
    let changed = false;
    for (const row of active) {
      if (row.status !== 'accepted' || !row.snapshot) continue;
      // eslint-disable-next-line no-await-in-loop
      const copy = await findMaterializedCopy(row.appointment_uid);
      if (!copy) {
        // Accepted on another device / before this install — materialize.
        // eslint-disable-next-line no-await-in-loop
        await materializeAcceptedInvitation(row);
        changed = true;
        continue;
      }
      // eslint-disable-next-line no-await-in-loop
      const patched = await patchCopyFromSnapshot(
        row.appointment_uid,
        row.snapshot,
        row.snapshot_version
      );
      changed = changed || patched;
    }
    bumpCalendarEvents();
    return changed;
  } catch {
    return false;
  }
}

// Registered at module load (chatNotification.js precedent — channelService
// listener arrays are safe before any socket exists). App.js reaches this
// module through CalendarView, so the watchers are armed at boot.
addNotificationListener((notif) => {
  if (typeof notif?.type === 'string' && notif.type.startsWith('appointment_')) {
    // Returned (not fire-and-forget) so the wiring test can await the real
    // handler; channelService ignores listener return values.
    return handleAppointmentNotification(notif);
  }
  return undefined;
});
addUserRejoinListener(() => {
  syncInvitations();
});

// One initial load per session — surfaces that render invitation state (the
// calendar, a chat card) call this on mount; the realtime + rejoin listeners
// keep it fresh afterwards. Reset with the session cache above.
let loadedOnce = false;
addSessionEndedListener(() => {
  loadedOnce = false;
});

export async function ensureInvitationsLoaded() {
  if (loadedOnce) return;
  const { isAuthenticated } = useAuthStore.getState();
  if (!isAuthenticated) return;
  loadedOnce = true;
  await syncInvitations();
}

/** The four appointment notification types (bell icon/action dispatch). */
export function isAppointmentNotificationType(type) {
  return (
    type === 'appointment_invitation' ||
    type === 'appointment_updated' ||
    type === 'appointment_cancelled' ||
    type === 'appointment_response'
  );
}
